import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../services/api';
import { Card, PageHeader, Spinner } from '../components/ui';
import { STAGE_COLORS, LEAD_STAGES } from '../utils/constants';
import toast from 'react-hot-toast';
import { useRole } from '../hooks/useRole';
import AccessDenied from '../components/AccessDenied';

const StatBox = ({ label, value, color = '#1B2A6B', sub }) => (
  <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
    <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</p>
    <p style={{ fontSize: 26, fontWeight: 800, color }}>{value ?? '—'}</p>
    {sub && <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</p>}
  </div>
);

const Bar = ({ label, value, max, color }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      <div style={{ width: 120, fontSize: 12, fontWeight: 600, color: '#475569', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 4, height: 18, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, background: color || '#1B2A6B', height: '100%', borderRadius: 4, transition: 'width 0.5s ease', display: 'flex', alignItems: 'center', paddingLeft: 6, minWidth: pct > 0 ? 4 : 0 }}>
          {pct > 10 && <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>{value}</span>}
        </div>
      </div>
      <div style={{ width: 32, fontSize: 12, fontWeight: 700, color: '#334155', textAlign: 'right', flexShrink: 0 }}>{value}</div>
    </div>
  );
};

export default function ReportsPage() {
  const { isIntern, isSupport } = useRole();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month'); // month | quarter | year

  useEffect(() => {
    if (isIntern || isSupport) return;
    reportsAPI.getDashboard({ period }).then(res => {
      setData(res.data?.data);
    }).catch(() => toast.error('Failed to load reports')).finally(() => setLoading(false));
  }, [period, isIntern, isSupport]);

  if (isIntern || isSupport) return <AccessDenied />;

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner size={40} /></div>;

  const s = data?.summary || {};
  const periodLabel = period === 'year' ? 'Year' : period === 'quarter' ? 'Quarter' : 'Month';
  const periodTotal = s.periodLeads ?? s.totalLeads;
  const maxStage = Math.max(...(data?.leadsByStage?.map(x => x.count) || [1]));
  const maxSource = Math.max(...(data?.leadsBySource?.map(x => x.count) || [1]));

  return (
    <div>
      <PageHeader
        title="Reports & Analytics"
        subtitle="Pipeline performance and team metrics"
        actions={
          <div style={{ display: 'flex', gap: 4 }}>
            {['month', 'quarter', 'year'].map(p => (
              <button key={p} onClick={() => setPeriod(p)} style={{
                padding: '6px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0',
                background: period === p ? '#1B2A6B' : '#fff',
                color: period === p ? '#fff' : '#64748b',
                cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', fontWeight: period === p ? 600 : 400
              }}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        }
      />

      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
        <StatBox label={`Leads This ${periodLabel}`} value={periodTotal} color="#1B2A6B" />
        <StatBox label="Active Pipeline" value={s.activePipeline} color="#059669" />
        <StatBox label={`Closed This ${periodLabel}`} value={s.closedThisMonth} color="#C9A84C" />
        <StatBox label="Conversion Rate" value={periodTotal ? `${Math.round(((s.closedThisMonth || 0) / periodTotal) * 100)}%` : '0%'} color="#7c3aed" />
        <StatBox label="Stale Leads" value={s.staleLeads} color="#f59e0b" sub="No activity 7d+" />
        <StatBox label="Avg Deal Value" value={s.avgDealValue ? `₹${Math.round(s.avgDealValue).toLocaleString('en-IN')}` : '—'} color="#1B2A6B" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Stage Funnel */}
        <Card>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B', marginBottom: 16 }}>Pipeline by Stage</h2>
          {data?.leadsByStage?.length ? data.leadsByStage.map(item => (
            <Bar key={item._id} label={item._id} value={item.count} max={maxStage} color={STAGE_COLORS[item._id]?.text || '#1B2A6B'} />
          )) : <p style={{ color: '#94a3b8', fontSize: 13 }}>No data</p>}
        </Card>

        {/* Source Breakdown */}
        <Card>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B', marginBottom: 16 }}>Leads by Source</h2>
          {data?.leadsBySource?.length ? data.leadsBySource.map(item => (
            <Bar key={item._id} label={item._id?.replace('_', ' ')} value={item.count} max={maxSource} color="#C9A84C" />
          )) : <p style={{ color: '#94a3b8', fontSize: 13 }}>No data</p>}
        </Card>
      </div>

      {/* Team Performance */}
      {data?.teamPerformance?.length > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B', marginBottom: 16 }}>Team Performance</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
                  {['Member', 'Role', 'Total Leads', 'Closed', 'Conversion', 'Pending Followups'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.teamPerformance.map(m => (
                  <tr key={m._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 28, height: 28, background: '#e0e7ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#1B2A6B' }}>
                          {m.name?.[0]}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{m.name}</span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b' }}>{m.sub_role}</td>
                    <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 700, color: '#1B2A6B' }}>{m.leadCount}</td>
                    <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 700, color: '#059669' }}>{m.closedCount || 0}</td>
                    <td style={{ padding: '10px 12px', fontSize: 13, color: '#7c3aed' }}>
                      {m.leadCount ? `${Math.round(((m.closedCount || 0) / m.leadCount) * 100)}%` : '0%'}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 13, color: m.overdueFollowups > 0 ? '#dc2626' : '#94a3b8' }}>
                      {m.overdueFollowups || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Customer Type Breakdown */}
      {data?.leadsByCustomerType?.length > 0 && (
        <Card>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B', marginBottom: 16 }}>Leads by Customer Type</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
            {data.leadsByCustomerType.map(item => (
              <div key={item._id} style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>{item._id}</p>
                <p style={{ fontSize: 22, fontWeight: 800, color: '#1B2A6B' }}>{item.count}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
